import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Vendor } from "assets/SideBarIcons/vendor.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { VendorProfileService } from "subModule/src/core/services";
import { DeleteData } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import { ChangeStatusVendorResponse } from "subModule/src/services/vendor/interface";
import SupplierSearchHeader from "components/Supplier/SupplierSearchHeader";

interface SearchFormData {
  [key: string]: string | any;
}
export default function SupplierListing() {
  const navigate = useNavigate();
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>([
    "title",
    "vendorCode",
    "email",
  ]);
  const [search, setSearch] = useState<any>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    title: "",
    vendorCode: "",
    email: "",
  });
  const [row, setRow] = useState<any>({});
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetVendorService({ pageNumber: pageNo, size: pageSize, ...search });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate("/supplier/" + row.id);
  const showRowView = (row: any) => navigate("/supplier/" + row.id);
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["title", "vendorCode", "email"]);
    setSearchFormData({ title: "", vendorCode: "", email: "" });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = value?.trim();
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_VENDOR_UPDATE")
      ? checkPrivileges("ROLE_VENDOR_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_VENDOR_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/supplier/create")}
        heading={t("vendors")}
        icon={<Vendor height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_VENDOR_CREATE")}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "title", label: "title", numeric: false, width: "22%" },
            {
              id: "vendorCode",
              label: "supplierCode",
              numeric: false,
              width: "14%",
            },
            { id: "email", label: "email", numeric: false, width: "22%" },
            {
              id: "phoneNumber",
              label: "phoneNumber",
              numeric: false,
              width: "16%",
            },
            ...(checkPrivileges("ROLE_VENDOR_ACTIVE")
              ? [
                  {
                    id: "active",
                    label: "status",
                    numeric: false,
                    width: "10%",
                  },
                ]
              : []),
            ...(adjustActions(actionslist).length
              ? [
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "8%",
                  },
                ]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_VENDOR_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          headerComponent={
            <SupplierSearchHeader
              searchFormData={searchFormData}
              handleChangeFilter={handleChangeFilter}
              handleChangeSearch={handleChangeSearch}
              handleSubmitSearch={handleSubmitSearch}
              clearSearch={clearSearch}
              search={search}
              {...{ filter }}
            />
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("vendor")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            VendorProfileService.deleteVendor((row as any).id)
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("vendor") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            VendorProfileService.changeStatusVendor(
              (row as any).id,
              row.active ? "inactive" : "active",
              {},
            )
              .then((response: ChangeStatusVendorResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
